// CompilationSteps.js
import { Box, makeStyles, Typography } from "@material-ui/core"
import React, { useState, useEffect } from "react"
// @ts-ignore
import { useHistory } from "react-router-dom"
import CustomStepper from "../ui-components/custom-stepper"
import useStore from "src/v2/utils/store"
import consts from "src/v2/utils/consts.json"
import { useAlertStore } from "src/v2/utils/alert-store"

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/background1.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    overflow: "hidden",
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up("sm")]: { paddingTop: theme.spacing(6) },
    [theme.breakpoints.up("md")]: { paddingTop: theme.spacing(8) },
  },
  content: {
    position: "relative",
    zIndex: 2,
    color: "white",
    textAlign: "center",
    maxWidth: 1000,
    margin: "0 auto",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up("sm")]: { paddingBottom: theme.spacing(6) },
    [theme.breakpoints.up("md")]: { paddingBottom: theme.spacing(8) },
  },
  compilationStepsContainer: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}))

// Custom hook for animated dots
const useAnimatedDots = (isActive) => {
  const [dots, setDots] = useState('')
  
  useEffect(() => {
    if (!isActive) {
      setDots('')
      return
    }
    
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '.'
        return prev + '.'
      })
    }, 500)
    
    return () => clearInterval(interval)
  }, [isActive])
  
  return dots
}

export default function CompilationSteps() {
  const classes = useStyles()
  const history = useHistory()
  const [activeStep, setActiveStep] = useState(0)
  const [aiSimulationStatus, setAiSimulationStatus] = useState({})
  const [compilationStatus, setCompilationStatus] = useState({})
  const setCompiling = useStore((state) => state.setCompiling)
  const lastCreatedReviewId = useStore((state) => state.lastCreatedReviewId)
  const setLastCreatedReviewId = useStore(
    (state) => state.setLastCreatedReviewId,
  )
  const compilationPayload = useStore((state) => state.compilationPayload)
  const getAiSimulationStatus = useStore((state) => state.getAiSimulationStatus)
  const getCompilationStatus = useStore((state) => state.getCompilationStatus)
  const runTestRunner = useStore((state) => state.runTestRunner)
  const showAlert = useAlertStore((state) => state.showAlert)

  // The step list is static. When the backend skips AI simulation generation
  // (and consequently simulation execution), those steps remain visible but
  // their labels are suffixed with "(skipped)" so the user can still see
  // every phase of the pipeline without the list "jumping" mid-run.
  const STEP_NAMES = [
    "Compiling the Code",
    "Saving Compilation Data",
    "Analyzing",
    "Generating simulations by AI agent",
    "Running tests",
    "Running simulations",
    "Generating Report",
    "Done",
  ]
  const steps = STEP_NAMES
  const stepIndexOf = (name) => STEP_NAMES.indexOf(name)

  // For animated dots during the AI simulation step.
  const aiSimStepIndex = stepIndexOf("Generating simulations by AI agent")
  const runTestsStepIndex = stepIndexOf("Running tests")
  const animatedDots = useAnimatedDots(
    aiSimStepIndex !== -1 && activeStep === aiSimStepIndex,
  )
  const runTestsDots = useAnimatedDots(activeStep === runTestsStepIndex)

  useEffect(() => {
    let cancelled = false
    let pollInterval = null

    const handleError = (message, title = "Error") => {
      showAlert &&
        showAlert({
          message,
          severity: "error",
          title,
        })
    }

    const runSteps = async () => {
      try {
        // Start the test runner (non-blocking)
        console.log('Starting test runner...')
        const testRunnerPromise = runTestRunner(compilationPayload, showAlert)
        
        // Wait a moment for the backend to generate the reviewId
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Poll compilation status from backend to update UI
        const pollStatus = async () => {
          if (cancelled) return
          
          try {
            // Get reviewId from store (it gets set by the test runner)
            const currentReviewId = useStore.getState().lastCreatedReviewId
            
            if (!currentReviewId) {
              console.log('Waiting for reviewId...')
              return
            }
            
            console.log(`Fetching compilation status for reviewId: ${currentReviewId}`)
            const status = await getCompilationStatus(currentReviewId)
            console.log('Compilation status:', status)
            setCompilationStatus(status)
            
            // Check for errors in any step and stop processing
            const errorSteps = Object.entries(status).find(([key, value]) => value?.status === 'error')
            if (errorSteps) {
              const [stepName, stepData] = errorSteps
              console.error(`Error in step ${stepName}:`, stepData.error)
              
              // Stop polling
              if (pollInterval) {
                clearInterval(pollInterval)
                pollInterval = null
              }
              
              // Show error to user
              handleError(
                stepData.error || `Error in ${stepName.replace(/_/g, ' ')}`,
                "Compilation Error"
              )
              
              setCompiling(false)
              return
            }
            
            // Determine the visible-stepper index purely from backend
            // status, using the static STEP_NAMES built above. Skipped steps
            // remain visible (with a "(skipped)" suffix) so we never need to
            // remap indices.
            const skippedNow =
              status.generating_simulations?.status === 'skipped'
            const NAMES = STEP_NAMES
            const idx = (name) => NAMES.indexOf(name)

            let newActiveStep = 0

            if (status.done?.status === 'done') {
              newActiveStep = NAMES.length - 1
              if (pollInterval) {
                clearInterval(pollInterval)
                pollInterval = null
              }
              setTimeout(() => {
                setCompiling(false)
                const actualReviewId = status.done?.savedReviewId || currentReviewId
                console.log(`Navigation: using review ID ${actualReviewId} (saved: ${status.done?.savedReviewId}, tracking: ${currentReviewId})`)
                if (actualReviewId) {
                  history.push(`${consts.ROUTES.REPORTS}/${actualReviewId}`)
                }
              }, 1000)
            } else if (
              status.generating_report?.status === 'working' ||
              status.generating_report?.status === 'done'
            ) {
              newActiveStep = idx("Generating Report")
            } else if (
              !skippedNow &&
              (status.running_simulations?.status === 'working' ||
                status.running_simulations?.status === 'done')
            ) {
              newActiveStep = idx("Running simulations")
            } else if (
              status.running_tests?.status === 'working' ||
              status.running_tests?.status === 'done'
            ) {
              newActiveStep = idx("Running tests")
            } else if (
              !skippedNow &&
              (status.generating_simulations?.status === 'working' ||
                status.generating_simulations?.status === 'done')
            ) {
              newActiveStep = idx("Generating simulations by AI agent")
            } else if (
              status.analyzing?.status === 'working' ||
              status.analyzing?.status === 'done'
            ) {
              newActiveStep = idx("Analyzing")
            } else if (
              status.saving?.status === 'working' ||
              status.saving?.status === 'done'
            ) {
              newActiveStep = idx("Saving Compilation Data")
            } else if (
              status.compiling?.status === 'working' ||
              status.compiling?.status === 'done'
            ) {
              newActiveStep = idx("Compiling the Code")
            }

            // -1 guard: if for some reason the label isn't present, keep prior
            if (newActiveStep < 0) newActiveStep = 0

            setActiveStep(newActiveStep)
          } catch (err) {
            console.error("Error polling compilation status:", err)
          }
        }
        
        // Start polling immediately and then every 1.5 seconds
        pollStatus() // First call immediately
        pollInterval = setInterval(pollStatus, 1500)
        
        // Wait for test runner to complete
        const result = await testRunnerPromise
        
        if (!result || result.error) {
          handleError(
            result?.error || "Test runner failed",
            "Test Runner Error"
          )
          if (pollInterval) {
            clearInterval(pollInterval)
          }
          return
        }

        // Save the reviewId from the result
        const reviewId = result.reviewId
        setLastCreatedReviewId(reviewId)
        console.log('Test runner completed with reviewId:', reviewId)
        
      } catch (err) {
        handleError(
          err?.message || "Test runner failed",
          "Test Runner Error"
        )
        if (pollInterval) {
          clearInterval(pollInterval)
        }
        return
      }
    }

    runSteps()
    
    return () => {
      cancelled = true
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
    // eslint-disable-next-line
  }, [history, setCompiling])

  // Add status polling for AI simulation generation (for step 3)
  useEffect(() => {
    let statusInterval
    
    // During AI simulation generation step (only exists when sims aren't skipped)
    if (aiSimStepIndex !== -1 && activeStep === aiSimStepIndex) {
      statusInterval = setInterval(async () => {
        const reviewId = useStore.getState().lastCreatedReviewId
        if (reviewId) {
          const status = await getAiSimulationStatus(reviewId)
          setAiSimulationStatus(status)
        }
      }, 2000) // Poll every 2 seconds (less frequent since backend handles it)
    }

    return () => {
      if (statusInterval) {
        clearInterval(statusInterval)
      }
    }
  }, [activeStep, getAiSimulationStatus])

  // Enhanced step description with animated status
  const getStepDescription = (stepIndex) => {
    // Show simulation animation only when this slot IS the AI sim step
    if (aiSimStepIndex !== -1 && stepIndex === aiSimStepIndex) {
      // Check if we have compilation status with simulation count
      // @ts-ignore
      if (compilationStatus?.generating_simulations) {
        // @ts-ignore
        const simStatus = compilationStatus.generating_simulations
        const totalSims = simStatus?.totalSimulations || 0
        const targetSims = simStatus?.targetSimulations || 0
        
        if (simStatus?.status === 'working') {
          // Show different messages based on progress
          if (totalSims === 0 && targetSims === 0) {
            // Just started, show "creating..." 
            return `${steps[stepIndex]} (creating${animatedDots})`
          } else if (totalSims === 0 && targetSims > 0) {
            // Know target but haven't created any yet
            return `${steps[stepIndex]} (creating ${targetSims} simulations${animatedDots})`
          } else if (targetSims > 0) {
            // Show progress: "X of Y created..."
            return `${steps[stepIndex]} (${totalSims} of ${targetSims} created${animatedDots})`
          } else {
            // Fallback to simple count
            return `${steps[stepIndex]} (${totalSims} created${animatedDots})`
          }
        } else if (simStatus?.status === 'done' && totalSims > 0) {
          return `${steps[stepIndex]} (${totalSims} created)`
        } else if (simStatus?.status === 'skipped') {
          return `${steps[stepIndex]} (skipped)`
        }
      }
      
      // Fallback to AI simulation status
      if (Object.keys(aiSimulationStatus).length > 0) {
        const statusEntries = Object.entries(aiSimulationStatus)
        const currentTask = statusEntries.find(([task, status]) => status === 'working')
        
        if (currentTask) {
          return `${steps[stepIndex]} (${currentTask[0]}${activeStep === aiSimStepIndex ? animatedDots : ''})`
        }
        
        // Check if all tasks are done
        const allDone = statusEntries.every(([task, status]) => status === 'done')
        if (allDone && statusEntries.length > 0) {
          return `${steps[stepIndex]} (Complete)`
        }
        
        // Check if there's an error
        const hasError = statusEntries.some(([task, status]) => status === 'error')
        if (hasError) {
          return `${steps[stepIndex]} (Error occurred)`
        }
      }
      
      // Default animation when no specific status
      if (activeStep === aiSimStepIndex) {
        return `${steps[stepIndex]}${animatedDots}`
      }
    }

    // "Running simulations" step: show "(skipped)" when the backend skipped it.
    if (stepIndex === stepIndexOf("Running simulations")) {
      // @ts-ignore
      if (compilationStatus?.running_simulations?.status === 'skipped') {
        return `${steps[stepIndex]} (skipped)`
      }
    }

    // "Running tests" step: show overall percentage progress while working.
    if (stepIndex === runTestsStepIndex) {
      // @ts-ignore
      const rt = compilationStatus?.running_tests
      if (rt?.status === 'working') {
        const completed = rt?.completedTests
        const total = rt?.totalTests
        const sub = rt?.subProgress
        if (typeof completed === 'number' && typeof total === 'number' && total > 0) {
          // Combine outer module progress with any inner sub-progress so the
          // percentage advances smoothly across the long-running v1 detector
          // suite instead of jumping from e.g. 86% to 100% in one step.
          // Each outer slot is worth 1/total; if a sub-suite is active, the
          // currently-running slot contributes (sub.completed / sub.total)
          // of its slot's share.
          let fraction = completed / total
          if (
            sub &&
            typeof sub.completed === 'number' &&
            typeof sub.total === 'number' &&
            sub.total > 0
          ) {
            const subFraction = Math.min(Math.max(sub.completed / sub.total, 0), 1)
            fraction += subFraction / total
          } else if (rt?.currentTest) {
            // No sub-progress reported, but a module is in-flight. Don't show
            // 0% on the very first tick — give partial credit (half of one slot).
            fraction += 0.5 / total
          }
          const percent = Math.min(99, Math.max(0, Math.floor(fraction * 100)))
          return `${steps[stepIndex]} (${percent}%${runTestsDots})`
        }
        return `${steps[stepIndex]}${runTestsDots}`
      }
      if (rt?.status === 'done') {
        return `${steps[stepIndex]} (100%)`
      }
    }

    return steps[stepIndex]
  }

  // Create enhanced steps array with dynamic descriptions
  const enhancedSteps = steps.map((step, index) => getStepDescription(index))

  return (
    <Box>
      {/* Banner section */}
      <Box className={classes.root}>
        <Box className={classes.content}>
          <Typography variant="h3" gutterBottom style={{ fontWeight: "bold" }}>
            Testing in progress - this takes time, but delivers better results
            than manual review, saving 200+ hours with broader coverage and
            higher quality
          </Typography>
        </Box>
      </Box>
      {/* Compilation steps section */}
      <Box mt={4} className={classes.compilationStepsContainer}>
        <CustomStepper steps={enhancedSteps} activeStep={activeStep} />
      </Box>
    </Box>
  )
}

